import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  Image, 
  Plus, 
  Edit, 
  Trash2, 
  Eye,
  EyeOff,
  ArrowLeft,
  Upload
} from 'lucide-react'
import CarouselEditor from '../components/CarouselEditor'

const AdminCarousel = () => {
  const [carousels, setCarousels] = useState([])
  const [loading, setLoading] = useState(true)
  const [showEditor, setShowEditor] = useState(false)
  const [editingCarousel, setEditingCarousel] = useState(null)
  const [isEditing, setIsEditing] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    // 检查用户是否已登录
    const token = localStorage.getItem('token')
    const userData = localStorage.getItem('user')
    
    if (!token || !userData) {
      navigate('/admin/login')
      return
    }

    loadCarousels()
  }, [navigate])

  const loadCarousels = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch('/api/carousel/all', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        if (data.success) {
          setCarousels(data.data)
        }
      }
    } catch (error) {
      console.error('加载轮播图列表失败:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleToggleStatus = async (carouselId, currentStatus) => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`/api/carousel/${carouselId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          is_active: !currentStatus
        })
      })

      if (response.ok) {
        setCarousels(carousels.map(carousel => 
          carousel.id === carouselId 
            ? { ...carousel, is_active: !currentStatus }
            : carousel
        ))
      }
    } catch (error) {
      console.error('切换轮播图状态失败:', error)
    }
  }

  const handleDelete = async (carouselId) => {
    if (!window.confirm('确定要删除这个轮播图吗？')) {
      return
    }

    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`/api/carousel/${carouselId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        setCarousels(carousels.filter(c => c.id !== carouselId))
      }
    } catch (error) {
      console.error('删除轮播图失败:', error)
    }
  }

  const handleCreate = () => {
    setEditingCarousel(null)
    setIsEditing(false)
    setShowEditor(true)
  }

  const handleEdit = (carousel) => {
    setEditingCarousel(carousel)
    setIsEditing(true)
    setShowEditor(true)
  }

  const handleSave = async (carouselData) => {
    try {
      const token = localStorage.getItem('token')
      const url = isEditing && editingCarousel 
        ? `/api/carousel/${editingCarousel.id}`
        : '/api/carousel'
      
      const method = isEditing && editingCarousel ? 'PUT' : 'POST'

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(carouselData)
      })

      if (response.ok) {
        const data = await response.json()
        if (data.success) {
          if (isEditing && editingCarousel) {
            // 更新现有轮播图
            setCarousels(carousels.map(c => 
              c.id === editingCarousel.id ? data.data : c
            ))
          } else {
            // 添加新轮播图
            setCarousels([...carousels, data.data])
          }
          setShowEditor(false)
          setEditingCarousel(null)
          setIsEditing(false)
        }
      }
    } catch (error) {
      console.error('保存轮播图失败:', error)
      alert('保存失败，请重试')
    }
  }

  const handleCancel = () => {
    setShowEditor(false)
    setEditingCarousel(null)
    setIsEditing(false)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">加载中...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 顶部导航栏 */}
      <nav className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="text-gray-500 hover:text-gray-700 flex items-center space-x-1"
              >
                <ArrowLeft size={16} />
                <span>返回仪表板</span>
              </button>
              <h1 className="text-xl font-bold text-gray-900">
                轮播图管理
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <button 
                onClick={handleCreate}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center space-x-2"
              >
                <Plus size={16} />
                <span>添加轮播图</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* 主要内容区域 */}
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {/* 统计信息 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">轮播图总数</p>
                  <p className="text-2xl font-bold text-gray-900">{carousels.length}</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Image className="text-blue-600" size={24} />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">活跃轮播图</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {carousels.filter(c => c.is_active).length}
                  </p>
                </div>
                <div className="p-3 bg-green-100 rounded-lg">
                  <Eye className="text-green-600" size={24} />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">隐藏轮播图</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {carousels.filter(c => !c.is_active).length}
                  </p>
                </div>
                <div className="p-3 bg-gray-100 rounded-lg">
                  <EyeOff className="text-gray-600" size={24} />
                </div>
              </div>
            </div>
          </div>

          {/* 轮播图列表 */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                轮播图列表
              </h2>
            </div>
            <div className="p-6">
              {carousels.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {carousels.map((carousel) => (
                    <div key={carousel.id} className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                      <div className="aspect-w-16 aspect-h-9 bg-gray-100">
                        {carousel.image_url ? (
                          <img 
                            src={carousel.image_url} 
                            alt={carousel.title}
                            className="w-full h-48 object-cover"
                          />
                        ) : (
                          <div className="w-full h-48 bg-gray-200 flex items-center justify-center">
                            <Image className="text-gray-400" size={32} />
                          </div>
                        )}
                      </div>
                      <div className="p-4">
                        <h3 className="font-medium text-gray-900 mb-2">
                          {carousel.title || '未命名轮播图'}
                        </h3>
                        <p className="text-sm text-gray-500 mb-3 line-clamp-2">
                          {carousel.description || '暂无描述'}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            carousel.is_active 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {carousel.is_active ? '已启用' : '已禁用'}
                          </span>
                          <div className="flex space-x-2">
                            <button 
                              onClick={() => handleToggleStatus(carousel.id, carousel.is_active)}
                              className={`p-1 rounded ${
                                carousel.is_active 
                                  ? 'text-yellow-600 hover:text-yellow-800' 
                                  : 'text-green-600 hover:text-green-800'
                              }`}
                            >
                              {carousel.is_active ? <EyeOff size={16} /> : <Eye size={16} />}
                            </button>
                            <button 
                              onClick={() => handleEdit(carousel)}
                              className="text-blue-600 hover:text-blue-800 p-1 rounded"
                            >
                              <Edit size={16} />
                            </button>
                            <button 
                              onClick={() => handleDelete(carousel.id)}
                              className="text-red-600 hover:text-red-800 p-1 rounded"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Image className="mx-auto text-gray-400" size={48} />
                  <p className="mt-4 text-gray-500">暂无轮播图数据</p>
                  <p className="text-sm text-gray-400 mb-6">添加轮播图以在首页展示</p>
                  <button 
                    onClick={handleCreate}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-md font-medium flex items-center space-x-2 mx-auto"
                  >
                    <Plus size={16} />
                    <span>添加第一个轮播图</span>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* 操作指南 */}
          <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="text-lg font-medium text-blue-900 mb-4">轮播图管理说明</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-800">
              <div className="flex items-start space-x-2">
                <Eye className="text-blue-600 mt-0.5" size={16} />
                <div>
                  <p className="font-medium">状态管理</p>
                  <p>启用/禁用轮播图来控制其在首页的显示</p>
                </div>
              </div>
              <div className="flex items-start space-x-2">
                <Upload className="text-blue-600 mt-0.5" size={16} />
                <div>
                  <p className="font-medium">图片要求</p>
                  <p>建议使用 1920×600 像素的图片以获得最佳显示效果</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 轮播图编辑器 */}
      {showEditor && (
        <CarouselEditor
          carousel={editingCarousel}
          onSave={handleSave}
          onCancel={handleCancel}
          isEditing={isEditing}
        />
      )}
    </div>
  )
}

export default AdminCarousel
